import { useState } from "react";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import { submitContact } from "../lib/api";
import { toast } from "sonner";

const ENQUIRY_TYPES = [
  { value: "general", label: "General enquiry" },
  { value: "support", label: "I need support" },
  { value: "story", label: "I'd like to share my story" },
  { value: "campaign", label: "Campaign / advocacy" },
  { value: "press", label: "Press / media" },
];

const SIDEBAR_ITEMS = [
  { border: "border-ember", title: "If a child is in immediate danger", body: "Call 999." },
  { border: "border-rule", title: "For confidential family advice", body: "Family Rights Group: 0808 801 0366. Free and specialist." },
  { border: "border-rule", title: "If you need someone to listen", body: "Samaritans: 116 123. Free, 24/7." },
  { border: "border-rule", title: "We are not lawyers", body: "We can't represent you in court. But we can help you find someone who can." },
];

const DIRECT_EMAILS = [
  { label: "General enquiries", address: "info@truth4parents.com" },
  { label: "Press & media", address: "press@truth4parents.com" },
  { label: "Families seeking support", address: "support@truth4parents.com" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  enquiry_type: "general",
  message: "",
  consent: false,
};

const LABEL_CLS = "block text-xs uppercase tracking-[0.18em] font-accent font-semibold text-ink mb-2";
const INPUT_CLS = "w-full border border-rule bg-linen px-4 py-3 text-base outline-none focus:border-river";

function LabeledField({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLS}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactSidebar() {
  return (
    <aside className="lg:col-span-4 space-y-10">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
          Or write directly
        </div>
        <ul className="mt-5 space-y-3" data-testid="contact-direct-emails">
          {DIRECT_EMAILS.map((e) => (
            <li key={e.address}>
              <div className="text-xs font-accent text-ink-muted uppercase tracking-wider">
                {e.label}
              </div>
              <a
                href={`mailto:${e.address}`}
                data-testid={`email-${e.address.split('@')[0]}-link`}
                className="font-serif text-lg text-river hover:text-ember transition-colors break-all"
              >
                {e.address}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
          Before you write
        </div>
        <ul className="mt-5 space-y-5 text-ink-muted leading-relaxed">
          {SIDEBAR_ITEMS.map((item) => (
            <li key={item.title} className={`border-l-2 ${item.border} pl-4`}>
              <strong className="text-ink font-serif text-lg block">{item.title}</strong>
              {item.body}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function ContactSuccess({ onReset }) {
  return (
    <div data-testid="contact-success" className="border border-river bg-white p-10 md:p-14">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        Thank you
      </div>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-ink leading-tight tracking-tight">
        Your message has been received.
      </h2>
      <p className="mt-5 text-ink-muted leading-relaxed">
        We read every message. We will write back as soon as we are able — usually within a few days. If your need is urgent, please use one of the phone lines on the left.
      </p>
      <button
        data-testid="contact-send-another-button"
        onClick={onReset}
        className="mt-8 inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-linen-alt transition-colors"
      >
        Send another message
      </button>
    </div>
  );
}

function ConsentRow({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 text-sm text-ink-muted leading-relaxed cursor-pointer">
      <input
        type="checkbox"
        data-testid="contact-consent-checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 flex-shrink-0 w-4 h-4 accent-river"
      />
      <span>
        I&rsquo;m happy for someone to reply to me at this email address. I understand this is not legal advice and that for my specific case I should speak to a solicitor.
      </span>
    </label>
  );
}

function ContactForm({ form, onChange, onSubmit, loading }) {
  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-7 border border-rule bg-white p-8 md:p-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LabeledField id="contact-name" label="Your name">
          <input
            id="contact-name"
            data-testid="contact-name-input"
            type="text"
            required
            value={form.name}
            onChange={onChange("name")}
            className={INPUT_CLS}
            placeholder="(first name is enough)"
          />
        </LabeledField>
        <LabeledField id="contact-email" label="Email">
          <input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={onChange("email")}
            className={INPUT_CLS}
            placeholder="you@example.com"
          />
        </LabeledField>
      </div>

      <LabeledField id="contact-enquiry" label="What's this about?">
        <select
          id="contact-enquiry"
          data-testid="contact-enquiry-select"
          value={form.enquiry_type}
          onChange={onChange("enquiry_type")}
          className={INPUT_CLS}
        >
          {ENQUIRY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </LabeledField>

      <LabeledField id="contact-message" label="Your message">
        <textarea
          id="contact-message"
          data-testid="contact-message-textarea"
          rows={7}
          required
          minLength={10}
          value={form.message}
          onChange={onChange("message")}
          className={`${INPUT_CLS} resize-y`}
          placeholder="Write as much or as little as feels right. There is no right way to do this."
        />
      </LabeledField>

      <ConsentRow checked={form.consent} onChange={onChange("consent")} />

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-button"
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-river text-linen px-8 py-4 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors disabled:opacity-60"
      >
        {loading ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

function useContactForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted before sending.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Message received. We'll be in touch.");
      setForm(EMPTY_FORM);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Sorry, something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    submitted,
    onChange,
    onSubmit,
    reset: () => setSubmitted(false),
  };
}

export default function ContactPage() {
  const { form, loading, submitted, onChange, onSubmit, reset } = useContactForm();

  return (
    <div data-testid="contact-page">
      <PageMeta
        title="Get in touch"
        description="Write to us — whether you're a family looking for a quiet place to start, a journalist, or a professional working in the system. We read every message."
        path="/contact"
      />
      <PageHeader
        overline="Get in touch"
        title="Write to us."
        lede="Whether you're a family looking for a quiet place to start, a journalist, or a professional working in the system — we read every message."
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <ContactSidebar />
          <div className="lg:col-span-8">
            {submitted ? (
              <ContactSuccess onReset={reset} />
            ) : (
              <ContactForm form={form} onChange={onChange} onSubmit={onSubmit} loading={loading} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
